import { ReactNode, useCallback, useState } from "react";
import { fetchScheduleApplication } from "@api/scheduleTemplate.api";
import { ScheduleDeleteJournalEntryMode } from "@common/types/domains/accounting/schedule";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { countScheduleApplicationJournalEntries } from "./countScheduleApplicationJournalEntries";
import { ScheduleChangeConfirmationModal } from "./ScheduleChangeConfirmationModal";
import {
    buildRemoveScheduleCopy,
    buildReplaceLostCopy,
    buildReplaceScheduleCopy,
    SCHEDULE_DELETE_REMOVE_LABEL,
    SCHEDULE_DELETE_REVERSE_LABEL,
    ScheduleChangeCopy,
    ScheduleChangeSource,
} from "./scheduleChangeCopy";

export interface CurrentScheduleApplication {
    scheduleApplicationId: string;
    scheduleTemplateId: string | null;
    scheduleName: string | null;
}

interface PickerTemplate {
    id: string;
    name: string;
}

type ConfirmState<TTemplate extends PickerTemplate> =
    | {
          kind: "replace";
          template: TTemplate;
          copy: ScheduleChangeCopy;
      }
    | { kind: "remove"; copy: ScheduleChangeCopy };

interface Options<TTemplate extends PickerTemplate> {
    source: ScheduleChangeSource;
    /** The schedule this source already has, or null for a first apply. */
    current: CurrentScheduleApplication | null;
    /** The surface's own apply, including its toasts; resolves to whether it succeeded. */
    applySchedule: (template: TTemplate) => Promise<boolean>;
    /**
     * Removes `current` through the lifecycle `delete` verb; resolves to
     * whether it succeeded. The mode says what happens to its journal entries:
     * `reverse` corrects earned ones in their own months and removes only
     * future ones, `remove` hard-deletes them all.
     */
    removeCurrent: (
        journalEntryMode: ScheduleDeleteJournalEntryMode,
    ) => Promise<boolean>;
}

/**
 * PRO-11569: a first apply is immediate, but replacing or removing a schedule
 * tears down its journal entries, so both stop at one confirm before anything
 * is written. Replace names both schedules and the exact entry count and
 * always reverses; Remove asks whether to reverse or hard-delete.
 */
export function useScheduleChange<TTemplate extends PickerTemplate>({
    source,
    current,
    applySchedule,
    removeCurrent,
}: Options<TTemplate>) {
    const { toast, error } = useToaster();
    const { workspaceId } = useScope();
    const [confirm, setConfirm] = useState<ConfirmState<TTemplate> | null>(
        null,
    );
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const loadJournalEntryCount = useCallback(async (): Promise<
        number | null
    > => {
        if (
            workspaceId == null ||
            current == null ||
            current.scheduleTemplateId == null
        ) {
            error("Failed to load journal entries");
            return null;
        }
        try {
            const application = await fetchScheduleApplication({
                workspaceId,
                templateId: current.scheduleTemplateId,
                applicationId: current.scheduleApplicationId,
            });
            return application
                ? countScheduleApplicationJournalEntries(application)
                : 0;
        } catch (fetchError) {
            error(
                fetchError instanceof Error
                    ? fetchError.message
                    : "Failed to load journal entries",
            );
            return null;
        }
    }, [current, error, workspaceId]);

    const onSelect = useCallback(
        async (template: TTemplate): Promise<boolean> => {
            if (current == null) {
                return await applySchedule(template);
            }
            if (template.id === current.scheduleTemplateId) {
                return true;
            }
            const journalEntryCount = await loadJournalEntryCount();
            if (journalEntryCount == null) {
                return true;
            }
            setConfirm({
                kind: "replace",
                template,
                copy: buildReplaceScheduleCopy({
                    source,
                    currentScheduleName: current.scheduleName,
                    newScheduleName: template.name,
                    journalEntryCount,
                }),
            });
            setIsConfirmOpen(true);
            return true;
        },
        [applySchedule, current, loadJournalEntryCount, source],
    );

    const onClear = useCallback(() => {
        if (current == null) {
            return;
        }
        setConfirm({
            kind: "remove",
            copy: buildRemoveScheduleCopy({
                currentScheduleName: current.scheduleName,
            }),
        });
        setIsConfirmOpen(true);
    }, [current]);

    const dismissConfirm = useCallback(() => setIsConfirmOpen(false), []);

    const confirmChange = useCallback(
        async (journalEntryMode: ScheduleDeleteJournalEntryMode) => {
            if (confirm == null) {
                return;
            }
            const didRemove = await removeCurrent(journalEntryMode);
            if (!didRemove) {
                return;
            }
            setIsConfirmOpen(false);
            if (confirm.kind === "remove") {
                toast("Schedule removed");
                return;
            }
            const didApply = await applySchedule(confirm.template);
            if (!didApply) {
                error(
                    buildReplaceLostCopy({
                        currentScheduleName: current?.scheduleName,
                    }),
                );
            }
        },
        [applySchedule, confirm, current, error, toast, removeCurrent],
    );

    const isRemove = confirm?.kind === "remove";
    const confirmModal: ReactNode = (
        <ScheduleChangeConfirmationModal
            show={isConfirmOpen}
            copy={confirm?.copy ?? null}
            confirmLabel={
                isRemove ? SCHEDULE_DELETE_REVERSE_LABEL : "Replace schedule"
            }
            alternative={
                isRemove
                    ? {
                          label: SCHEDULE_DELETE_REMOVE_LABEL,
                          variant: "destructive",
                          onConfirm: () =>
                              confirmChange(
                                  ScheduleDeleteJournalEntryMode.remove,
                              ),
                      }
                    : undefined
            }
            onConfirm={() =>
                confirmChange(ScheduleDeleteJournalEntryMode.reverse)
            }
            onDismiss={dismissConfirm}
        />
    );

    return { onSelect, onClear, confirmModal };
}
